import React, {useEffect, useMemo, useState} from 'react';
import {Checkbox, DatePicker, Flex, message, Popconfirm, Typography} from "antd";
import dayjs, {Dayjs} from "dayjs";
import useSWR from "swr";
import {api, HydraData} from "../../api/apiProvider";
import urls from "../../api/urls";
import {useParams, useSearchParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {BriefGroupType, BriefManager, StageStatus} from "../../api/types";
import IconCalendarGrey from "../ui/icons/IconCalendarGrey";
import IconCalendar from "../ui/icons/IconCalendar";
import {DefaultOptionType} from "antd/es/select";
import IconPersonGrey from "../ui/icons/IconPersonGrey";
import IconPerson from "../ui/icons/IconPerson";
import IconPersonWhite from "../ui/icons/IconPersonWhite";
import getBriefParamsString from "../../api/params";
import Roles from "../../roles/Roles";

const {Text} = Typography;

export enum State {
  NotStarted = 'not-started',
  InWork = 'in-work',
  Completed = 'completed',
}

export enum StateDate {
  Overdue = 'overdue',
  Advance = 'advance',
  Plan = 'plan',
}

interface Stage {
  dateEnd: Dayjs,
  dateEndPlan: Dayjs,
  id: number,
  duration: number,
  name: string,
  status: StageStatus | null,
  briefManager: BriefManager | null,
  briefGroupType: BriefGroupType | null,
  selected: boolean,
  active: boolean,
  state: State,
  stateDate: StateDate,
  diff: number,
}

const updateStage = async (id: number, data: any) => {
  return await api.patch(`${urls.briefStages}/${id}`, {arg: data})
};

const updateBrief = async (id: number, data: any) => {
  return await api.patch(`${urls.briefs}/${id}`, {arg: data})
};

const dateFormat = 'YYYY-MM-DD';

interface Brief {
  isStagesAccepted: boolean,
  setupValidation: boolean,
}

export default function Stages() {
  const {t} = useTranslation();
  const routeParams = useParams();
  const [searchParams] = useSearchParams();
  const briefId = Number(routeParams.id) || Number(searchParams.get('id')) || 0;
  const [stages, setStages] = useState<Stage[]>([]);
  const [statusOptions, setStatusOptions] = useState<DefaultOptionType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activeStages, setActiveStages] = useState<number[]>([]);
  const [selectedFilterGroupTypes, setSelectedFilterGroupTypes] = useState<number[]>([]);
  const [brief, setBrief] = useState<Brief>();

  const {
    data: stagesData,
    mutate: mutateStages,
    isValidating: isStagesValidating
  } = useSWR<HydraData>(`${urls.briefStages
  }?brief.id=${briefId}&t[]=briefStage&t[]=dict:stage&t[]=dict:stage_status&t[]=brief_manager&t[]=briefStage:briefManagers&t[]=dict`, api.get, {
    revalidateOnFocus: true
  });

  const {
    data: statuses,
  } = useSWR<HydraData>(urls.stageStatuses, api.get, {
    revalidateOnFocus: false
  });

  const {
    data: briefGroupTypes,
  } = useSWR<HydraData>(urls.briefGroupTypes, api.get, {
    revalidateOnFocus: false
  });

  const {
    data: briefData,
    mutate: mutateBrief,
  } = useSWR<HydraData>(briefId > 0 && briefId !== 0 ? `${urls.briefs}/${briefId}?${getBriefParamsString()}` : null, api.get, {
    revalidateOnFocus: false
  });

  useEffect(() => {
    if (briefData && briefData.data) {
      mutateStages();
      setBrief({
        isStagesAccepted: briefData.data.isStagesAccepted,
        setupValidation: briefData.data.setupValidation,
      })
    }
  }, [briefData]);

  const groupTypes = useMemo(() => {
    if (!briefGroupTypes || !briefGroupTypes.data) {
      return []
    }

    return briefGroupTypes.data.map((briefGroupType: BriefGroupType) => {
      return briefGroupType
    })
  }, [briefGroupTypes]);

  useEffect(() => {
    if (statuses && statuses.data) {
      setStatusOptions(statuses.data.map((status: StageStatus) => {
        return {
          value: status.id,
          label: status.name,
        }
      }))
    }
  }, [statuses]);

  const getState = (stage: Stage, stages: Stage[]) => {
    if (dayjs(stage.dateEnd).isBefore(dayjs())) {
      return State.Completed
    }

    const currentStage = stages.find((stage: Stage) => dayjs(stage.dateEnd).isAfter(dayjs()) || dayjs(stage.dateEnd).isSame(dayjs(), 'day'));

    if (currentStage && currentStage.id === stage.id) {
      return State.InWork
    }

    return State.NotStarted
  };

  const getStateDate = (stage: Stage) => {
    if (dayjs(stage.dateEnd).isBefore(dayjs(stage.dateEndPlan))) {
      return StateDate.Advance
    }

    if (dayjs(stage.dateEnd).isAfter(dayjs(stage.dateEndPlan))) {
      return StateDate.Overdue
    }

    /*if (dayjs(stage.dateEnd).isSame(dayjs(stage.dateEndPlan), 'day')) {
      return StateDate.Plan
    }*/

    return StateDate.Plan
  };

  useEffect(() => {
    if (stagesData && stagesData.data) {
      const stageItems: Stage[] = [];

      for (let stage of stagesData.data) {
        const endDate = dayjs(stage.dateEnd);
        const dateEndPlan = dayjs(stage.dateEndPlan);
        const stageItem: Stage = {
          dateEnd: endDate,
          dateEndPlan: dateEndPlan,
          id: stage.id,
          name: stage.stageType.name,
          duration: stage.duration,
          status: stage.status,
          briefManager: stage.briefManager,
          briefGroupType: stage.briefGroupType,
          selected: false,
          active: false,
          state: getState(stage, stagesData.data),
          stateDate: getStateDate(stage),
          diff: dayjs(stage.dateEnd).diff(dayjs(stage.dateEndPlan), 'day'),
        };

        stageItems.push(stageItem)
      }

      setStages(stageItems)
    }
  }, [stagesData]);

  const saveStage = (stage: Stage) => {
    if (!Roles.hasAccess('canSetStage')) {
      return
    }

    setIsLoading(true);
    if (stage.id && Number.isInteger(stage.id)) {
      const date = brief?.isStagesAccepted
        ? (stage.dateEnd ? dayjs(stage.dateEnd).format(dateFormat) : null)
        : (stage.dateEndPlan ? dayjs(stage.dateEndPlan).format(dateFormat) : null);
      updateStage(Number(stage.id), {
        dateEnd: date,
        status: stage.status?.["@id"],
        briefGroupType: stage.briefGroupType?.["@id"] || null,
      }).then(values => {
        message.success(t('common.saved'));
        mutateStages();
        setIsLoading(false)
      }).catch((response) => {
        message.error(response['hydra:description']);
        setIsLoading(false)
      })
    }
  };

  const getPersonIcon = (stage: Stage, groupType: BriefGroupType) => {
    if (isStageGroupType(stage, groupType)) {
      if (stage.stateDate === StateDate.Overdue) {
        return <IconPersonGrey/>
      }

      return <IconPersonWhite/>
    }

    return <IconPerson/>
  };

  const isStageGroupType = (stage: Stage, groupType: BriefGroupType) => {
    return stage.briefGroupType && stage.briefGroupType.id === groupType.id
  };

  const toggleActive = (stage: Stage) => {
    const stages = Array.from(activeStages);
    if (stages.includes(stage.id)) {
      stages.splice(stages.indexOf(stage.id), 1);
      setActiveStages(stages)
    } else {
      setActiveStages([
        ...activeStages,
        stage.id
      ])
    }
  };

  const isActive = (stage: Stage) => {
    return activeStages.includes(stage.id)
  };

  const isCompleted = (stage: Stage) => stage.state === State.Completed;

  const isInFilter = (stage: Stage) => stage.briefGroupType && selectedFilterGroupTypes.includes(stage.briefGroupType.id);

  const handleDateChange = (stage: Stage, date: Dayjs) => {
    const newStages = Array.from(stages);
    for (let item of newStages) {
      if (item.id === stage.id) {
        item.dateEnd = date;
        saveStage(item)
      }
    }
  };

  const handleAccepted = (value: boolean) => {
    setIsLoading(true);
    updateBrief(briefId, {
      isStagesAccepted: Boolean(value),
    }).then(values => {
      message.success(t('common.saved'));
      mutateBrief();
      setIsLoading(false)
    }).catch((response) => {
      message.error(response['hydra:description']);
      setIsLoading(false)
    })
  };

  const handlePlanDateChange = (stage: Stage, date: Dayjs) => {
    const newStages = Array.from(stages);
    for (let item of newStages) {
      if (item.id === stage.id) {
        item.dateEndPlan = date;
        saveStage(item)
      }
    }
  };

  const handleGroupTypeSet = (stage: Stage, groupType: BriefGroupType) => {
    const newStages = Array.from(stages);
    for (let item of newStages) {
      if (item.id === stage.id) {
        item.briefGroupType = item.briefGroupType?.id === groupType.id ? null : groupType;
        saveStage(item)
      }
    }
  };

  const setFilter = (groupType: BriefGroupType) => {
    const newValues = Array.from(selectedFilterGroupTypes);
    if (newValues.includes(groupType.id)) {
      newValues.splice(newValues.indexOf(groupType.id), 1);
      setSelectedFilterGroupTypes(newValues)
    } else {
      setSelectedFilterGroupTypes([
        ...selectedFilterGroupTypes,
        groupType.id
      ])
    }
  };

  const disableWeekends = (current: Dayjs) => {
    return current.day() === 0 || current.day() === 6
  };

  return (
    <>
      <Flex vertical gap={'middle'}>
        <Flex vertical gap={8}>
          <Flex gap={4} wrap={'wrap'}>
            {groupTypes.map((groupType: BriefGroupType) =>
              <Flex
                className={'stages__item-manager '
                  + (selectedFilterGroupTypes.includes(groupType.id) ? 'stages__item-manager_current ' : '')}
                align={'center'}
                onClick={(event) => {
                  event.stopPropagation();
                  setFilter(groupType)
                }}
                key={groupType.id}
              >
                {selectedFilterGroupTypes.includes(groupType.id) ? <IconPersonWhite/> : <IconPerson/>}
                {groupType.name}
              </Flex>
            )}
          </Flex>
          <Flex gap={5} align={'center'}>
            <Popconfirm
              title={t('stages.acceptPlanDates')}
              description={t('stages.datesWillBlocked')}
              onConfirm={() => handleAccepted(!brief?.isStagesAccepted)}
              onCancel={() => {
              }}
              okText={t('common.yes')}
              cancelText={t('common.no')}
              disabled={brief?.isStagesAccepted}
            >
              <Checkbox
                checked={brief?.isStagesAccepted}
                onChange={event => {
                  if (brief?.isStagesAccepted) {
                    handleAccepted(event.target.checked)
                  }
                }}
                disabled={brief?.isStagesAccepted || !Roles.hasAccess('canSetStage')}
              />
            </Popconfirm>
            <span>{t('stages.acceptPlanDates')}</span>
          </Flex>
        </Flex>
        <Flex className={'stages__items'} vertical>
          <Flex justify={'space-between'}>
            <div/>
            <Flex style={{width: '200px'}} justify={'space-between'}>
              <Flex style={{width: '100px'}} justify={'center'}>{t('stages.plan')}</Flex>
              <Flex style={{width: '100px'}} justify={'center'}>{t('stages.fact')}</Flex>
            </Flex>
          </Flex>

          {stages.map(stage => (
            <Flex
              className={'stages__item ' + (stage.state) + ' ' + (stage.stateDate) + (isInFilter(stage) ? ' stages__item_filtered' : '')}
              gap={'small'}
              align={'center'}
              key={stage.id}
              vertical
              onClick={() => toggleActive(stage)}
            >
              <Flex justify={'space-between'} className={'stages__item-content'}>
                <Flex gap={4}>
                  <Flex className={'stages__item-text'} align={'center'}>
                    <Text ellipsis={{tooltip: stage.name}} style={{width: '400px'}}>
                      <Flex gap={'small'}>
                        {stage.name}
                        <Flex
                          className={'stages__item-duration'}
                          gap={6}
                          align={'center'}
                          style={{whiteSpace: 'nowrap'}}
                        >
                          {stage.duration} {stage.diff !== 0 ?
                          <span>(<span className={'stages__date-diff'}>{(stage.diff > 0 ? '+' : '') + stage.diff}</span>)</span> : ''} {t('stages.day')}
                        </Flex>
                      </Flex>
                    </Text>
                  </Flex>
                </Flex>
                <Flex gap={4}>
                  <Flex>
                    <DatePicker
                      format={'DD.MM.YY'}
                      size={'small'}
                      value={stage.dateEndPlan}
                      suffixIcon={isCompleted(stage) ? <IconCalendarGrey/> : <IconCalendar/>}
                      onChange={value => handlePlanDateChange(stage, value)}
                      disabled={brief?.isStagesAccepted|| !Roles.hasAccess('canSetStage')}
                      disabledDate={disableWeekends}
                      allowClear={false}
                    />
                  </Flex>
                  <Flex className={'stages__fact-date'}>
                    <DatePicker
                      format={'DD.MM.YY'}
                      size={'small'}
                      value={stage.dateEnd}
                      suffixIcon={isCompleted(stage) ? <IconCalendarGrey/> : <IconCalendar/>}
                      onChange={value => handleDateChange(stage, value)}
                      disabledDate={disableWeekends}
                      disabled={!brief?.isStagesAccepted|| !Roles.hasAccess('canSetStage')}
                      allowClear={false}
                    />
                  </Flex>
                </Flex>
              </Flex>
              {isActive(stage) &&
                <>
                  <Flex justify={'space-between'} style={{width: '100%'}} align={'flex-start'} gap={'middle'}>

                    <Flex gap={4} wrap={'wrap'} justify={'flex-end'}>
                      {groupTypes.map((groupType: BriefGroupType) =>
                        <Flex
                          className={'stages__item-manager '
                            + (isStageGroupType(stage, groupType) ? 'stages__item-manager_current ' : '')
                            + (isStageGroupType(stage, groupType) && (stage.stateDate === StateDate.Overdue ? 'stages__item-manager_expired' : ''))}
                          align={'center'}
                          onClick={(event) => {
                            event.stopPropagation();

                            handleGroupTypeSet(stage, groupType)
                          }}
                        >
                          {getPersonIcon(stage, groupType)}
                          {groupType.name}
                        </Flex>
                      )}
                    </Flex>
                  </Flex>
                </>
              }
            </Flex>
          ))}
        </Flex>
      </Flex>
    </>
  )
}
